import React, { useState, useEffect, useCallback, useRef } from 'react';
import { connect, useDispatch } from 'react-redux';
import Header from '../Header/Header';
import history from "../Routes/history";
import ConfirmMail from '../Mail/ConfirmMail';
import MailParams from './MailParams';
import GenPdf from '../GenPdf';
import InformationDialog from '../InformationDialog';
import "../../Styles/Mailing/SendMail.scss"

const SendMail = ({ user }) => {
  const dispatch = useDispatch();
  const isMountedRef = useRef(true);

  const [mailData, setMailData] = useState({
    sender: "",
    password: "",
    to: "",
    cc: "",
    cci: "",
    subject: "",
    text: `Bonjour,
Veuillez trouver ci-joint
Bien cordialement,`,
    files: [],
    selectedDossier: "",
  });

  const [uiState, setUiState] = useState({
    loading: false,
    disableButton: false,
    openDialog: false,
    openInfoDialog: false,
    pdf: false,
    image: false,
    file: null,
    imageFile: null,
    infoMessage: "",
    countdown: null,
    showCountdown: false,
  });

  // État pour suivre les fichiers ajoutés par type
  const [fileMetadata, setFileMetadata] = useState(new Map());

  useEffect(() => {
    if (!mailData.selectedDossier) return;
    const getContractInfos = async () => {
      try {
        const response = await fetch(`/api/devis/getMailMo/${mailData.selectedDossier}`);
        if (!response.ok) {
          console.error("Database query failed");
          return null;
        }
        const data = await response.json();
        if (data.length > 0) {
          setMailData(prev => ({ ...prev, to: data[0].mail }))
        };
      } catch (error) {
        console.error("Error fetching dossier:", error);
        return null;
      }
    };

    getContractInfos();
  }, [mailData.selectedDossier]);

  useEffect(() => {
    return () => {
      isMountedRef.current = false;
    };
  }, []);

  const safeSetUiState = useCallback((updater) => {
    if (isMountedRef.current) {
      setUiState(updater);
    }
  }, []);

  const safeSetMailData = useCallback((updater) => {
    if (isMountedRef.current) {
      setMailData(updater);
    }
  }, []);

  const getRedirectPath = useCallback(() => {
    return "/mailing/suivi-mail";
  }, []);

  // Fonction pour démarrer le décompte
  const startCountdown = useCallback(() => {
    safeSetUiState(prev => ({
      ...prev,
      countdown: 5,
      showCountdown: true
    }));
  }, [safeSetUiState]);

  // Gestion du countdown
  useEffect(() => {
    let countdownInterval = null;

    if (uiState.showCountdown && uiState.countdown !== null) {
      if (uiState.countdown > 0) {
        countdownInterval = setTimeout(() => {
          safeSetUiState(prev => {
            if (!isMountedRef.current) {
              return prev;
            }

            if (prev.countdown <= 1) {
              const redirectPath = getRedirectPath();
              if (redirectPath) {
                history.push(redirectPath);
              }
              return {
                ...prev,
                countdown: 0,
                showCountdown: false
              };
            }

            return {
              ...prev,
              countdown: prev.countdown - 1
            };
          });
        }, 1000);
      }
    }

    return () => {
      if (countdownInterval) {
        clearTimeout(countdownInterval);
      }
    };
  }, [uiState.showCountdown, uiState.countdown, safeSetUiState, getRedirectPath]);

  // Pour la prop information du dialog
  const getDialogInformation = () => {
    if (uiState.showCountdown && uiState.countdown !== null) {
      return (
        <>
          {uiState.infoMessage}
          <br />
          Redirection dans {uiState.countdown} seconde{uiState.countdown > 1 ? 's' : ''}...
        </>
      );
    }
    return uiState.infoMessage;
  };

  useEffect(() => {
    if (user.length > 0 && user[0].email) {
      const userEmail = user[0].email;
      safeSetMailData(prev => ({
        ...prev,
        sender: userEmail,
        cc: "coordogec@coordogec.fr, jeannette.wonje@coordogec.fr, jacob.ilunga@coordogec.fr, slimane.lami@coordogec.fr"
      }));
    }
  }, [user, safeSetMailData]);

  useEffect(() => {
    if (user.length > 0) {
      safeSetMailData(prev => ({ ...prev, sender: user[0].email }));
    }
  }, [user, safeSetMailData]);

  // Extraction des emails d'une chaîne de caractères
  const extractEmails = (inputString) => {
    const emailRegex = /[\w.-]+@[\w-]+(?:\.[\w-]{2,})+/g;
    const emails = inputString.match(emailRegex);
    return emails ? emails.join(", ") : "";
  };

  // Gestion des changements dans les champs de formulaire
  const handleChange = (event) => {
    const { name, value } = event.target;
    setMailData(prev => ({ ...prev, [name]: value }));
    if (name === "selectedDossier") setMailData(prev => ({ ...prev, subject: value ? `DOSSIER N° ${value.split("").join("-")} ` : "" }));
  };

  // Gestion des changements de fichiers (fichiers manuels)
  const handleFileChange = (event) => {
    const selectedFiles = Array.from(event.target.files);
    const newMetadata = new Map(fileMetadata);

    selectedFiles.forEach(file => {
      newMetadata.set(file.name, {
        type: 'manual',
        source: 'manual'
      });
    });

    setFileMetadata(newMetadata);
    setMailData(prev => ({
      ...prev,
      files: [...prev.files, ...selectedFiles]
    }));
    event.target.value = '';
  };

  // Suppression d'un fichier spécifique
  const handleRemoveFile = (indexToRemove) => {
    const fileToRemove = mailData.files[indexToRemove];
    if (fileToRemove) {
      const newMetadata = new Map(fileMetadata);
      newMetadata.delete(fileToRemove.name);
      setFileMetadata(newMetadata);
    }

    setMailData(prev => ({
      ...prev,
      files: prev.files.filter((_, index) => index !== indexToRemove)
    }));
  };

  // Sélection automatique de fichiers par type
  const handleAutoSelect = async (documentType) => {
    safeSetUiState(prev => ({
      ...prev,
      disableButton: true
    }));

    try {
      const newMetadata = new Map(fileMetadata);
      let newFiles = [];
      let success = false;

      if (documentType === 'contrat') {
        const response = await fetch(`/api/contrat/showPDF`, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'Accept': 'application/pdf'
          },
          body: JSON.stringify({
            ndossier: mailData.selectedDossier,
            type: 'pdf'
          })
        });

        if (response.ok) {
          const fileName = response.headers.get('X-FileName') || `Contrat de mission N°${mailData.selectedDossier}.pdf`;
          const blob = await response.blob();
          const file = new File([blob], fileName, { type: 'application/pdf' });

          newFiles = [file];
          newMetadata.set(fileName, {
            type: documentType,
            source: 'auto'
          });
          success = true;

          safeSetUiState(prev => ({
            ...prev,
            infoMessage: `Fichier contrat ajouté avec succès : ${fileName}`,
            disableButton: false,
            openInfoDialog: true
          }));
        } else {
          throw new Error(`Aucun contrat trouvé pour le dossier ${mailData.selectedDossier}`);
        }
      } else {
        // Pour tous les autres types (devis, pgc, prediuo, diuo, vic, rj)
        const apiEndpoints = {
          devis: '/api/devis/getDevisPdf',
          pgc: '/api/pgc/getPGCPdf',
          prediuo: '/api/prediuo/getPreDIUOPdf',
          diuo: '/api/diuo/getDIUOPdf',
          vic: '/api/vic/getVICsPdf',
          rj: '/api/rj/getRJPdf'
        };

        const response = await fetch(apiEndpoints[documentType], {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'Accept': 'application/json'
          },
          body: JSON.stringify({
            ndossier: mailData.selectedDossier,
            type: 'pdf'
          })
        });

        if (response.ok) {
          const data = await response.json();

          if (data.files && data.files.length > 0) {
            newFiles = await Promise.all(
              data.files.map(async (fileInfo) => {
                const fileResponse = await fetch(fileInfo.url);
                const blob = await fileResponse.blob();
                const file = new File([blob], fileInfo.name, { type: 'application/pdf' });

                newMetadata.set(fileInfo.name, {
                  type: documentType,
                  source: 'auto'
                });

                return file;
              })
            );
            success = true;

            const displayNames = {
              devis: 'devis',
              pgc: 'PGC',
              prediuo: 'Pré DIUO',
              diuo: 'DIUO',
              vic: 'VIC',
              rj: 'RJ'
            };

            safeSetUiState(prev => ({
              ...prev,
              infoMessage: `${newFiles.length} fichier(s) ${displayNames[documentType]} ajouté(s) avec succès`,
              disableButton: false,
              openInfoDialog: true
            }));
          } else {
            throw new Error(`Aucun fichier ${documentType.toUpperCase()} trouvé pour le dossier ${mailData.selectedDossier}`);
          }
        } else {
          throw new Error(`Erreur HTTP: ${response.status}`);
        }
      }

      // Mettre à jour les fichiers et métadonnées seulement si succès
      if (success && newFiles.length > 0) {
        setFileMetadata(newMetadata);
        setMailData(prev => ({
          ...prev,
          files: [...prev.files, ...newFiles]
        }));
      }

      // Retourner le succès pour que MailParams sache si ça a marché
      return success;

    } catch (error) {
      console.error('Erreur lors de la sélection automatique:', error);

      let errorMessage = `Erreur lors de la récupération des fichiers ${documentType.toUpperCase()}`;

      if (error.message.includes('404')) {
        errorMessage = `Le fichier ${documentType.toUpperCase()} est introuvable pour le dossier ${mailData.selectedDossier}`;
      } else if (error.message.includes('500')) {
        errorMessage = `Erreur serveur lors de la récupération du fichier ${documentType.toUpperCase()}`;
      } else if (error.message.includes('Aucun')) {
        errorMessage = error.message;
      }

      safeSetUiState(prev => ({
        ...prev,
        infoMessage: errorMessage,
        disableButton: false,
        openInfoDialog: true
      }));

      // Relancer l'erreur pour que MailParams puisse la gérer
      throw error;
    }
  };

  const handleSubmit = async () => {
    safeSetUiState(prev => ({ ...prev, disableButton: true }));

    try {
      const formData = new FormData();

      const toFormatted = extractEmails(mailData.to);
      const ccFormatted = extractEmails(mailData.cc);
      const cciFormatted = extractEmails(mailData.cci);

      const emailContent = `
        <div style="font-family: Aptos, Arial, sans-serif; font-size: 14px; line-height: 1.6;">
          ${mailData.text.replace(/\n/g, '<br>')}
        </div>
      `;

      formData.append("ndossier", mailData.selectedDossier);
      formData.append("sender", mailData.sender);
      formData.append("password", mailData.password);
      formData.append("to", toFormatted.split(",").map(email => email.trim()));
      formData.append("cc", ccFormatted.split(",").map(email => email.trim()));
      formData.append("cci", cciFormatted.split(",").map(email => email.trim()));
      formData.append("subject", mailData.subject);
      formData.append("text", emailContent);

      mailData.files.forEach((file, index) => {
        formData.append(`attachment_${index}`, file);
      });
      formData.append("filesCount", mailData.files.length);

      const response = await fetch('/api/mail/sendMailPdfs', {
        method: 'POST',
        body: formData
      });

      const result = await response.text();

      safeSetMailData(prev => ({
        ...prev,
        to: toFormatted,
        cc: ccFormatted,
        cci: cciFormatted,
        text: result === 'OK' ? '' : prev.text
      }));

      const isSuccess = result === 'OK';
      const message = isSuccess ? 'Votre mail a bien été envoyé avec succès' : 'Une erreur est survenue lors de l\'envoi du mail';

      safeSetUiState(prev => ({
        ...prev,
        infoMessage: message,
        openInfoDialog: true,
        disableButton: false
      }));

      if (isSuccess) {
        // Réinitialiser les métadonnées des fichiers
        setFileMetadata(new Map());
        startCountdown();
      }

    } catch (error) {
      console.error('Erreur lors de l\'envoi:', error);
      safeSetUiState(prev => ({
        ...prev,
        infoMessage: 'Une erreur est survenue lors de l\'envoi du mail',
        openInfoDialog: true,
        disableButton: false
      }));
    }
  };

  const handleResetForm = useCallback(() => {
    safeSetMailData(prev => ({
      sender: user.length > 0 && user[0].email ? user[0].email : "",
      password: "",
      to: "",
      cc: user.length > 0 && user[0].email ? "coordogec@coordogec.fr, jeannette.wonje@coordogec.fr, jacob.ilunga@coordogec.fr, slimane.lami@coordogec.fr" : "",
      cci: "",
      subject: "",
      text: `Bonjour,
Veuillez trouver ci-joint
Bien cordialement,`,
      files: [],
      selectedDossier: "",
    }));

    setFileMetadata(new Map());

    safeSetUiState(prev => ({
      ...prev,
      loading: false,
      disableButton: false,
      openDialog: false,
      openInfoDialog: false,
      pdf: false,
      image: false,
      file: null,
      imageFile: null,
      infoMessage: "",
      countdown: null,
      showCountdown: false,
    }));
  }, [user, safeSetMailData, safeSetUiState]);

  // Affichage du PDF pour un fichier spécifique
  const showPDF = async (file) => {
    if (file && file.type === 'application/pdf' && isMountedRef.current) {
      safeSetUiState(prev => ({ ...prev, file: file, pdf: true }));
    }
  };

  // Affichage d'une image pour un fichier spécifique
  const showImage = async (file) => {
    if (file && file.type.startsWith('image/') && isMountedRef.current) {
      safeSetUiState(prev => ({ ...prev, imageFile: file, image: true }));
    }
  };

  // Fonction pour obtenir le type de fichier basé sur les métadonnées
  const getFileType = (fileName) => {
    const metadata = fileMetadata.get(fileName);
    if (metadata) {
      return metadata.type;
    }

    // Fallback: détection basée sur le nom du fichier
    const lowerName = fileName.toLowerCase();
    if (lowerName.includes('devis')) return 'devis';
    if (lowerName.includes('contrat')) return 'contrat';
    if (lowerName.includes('pgc')) return 'pgc';
    if (lowerName.includes('prediuo') || lowerName.includes('pre-diuo')) return 'prediuo';
    if (lowerName.includes('diuo')) return 'diuo';
    if (lowerName.includes('vic')) return 'vic';
    if (lowerName.includes('rj')) return 'rj';
    return 'manual';
  };

  const handleOpenDialog = () => safeSetUiState(prev => ({ ...prev, openDialog: true }));
  const handleCloseDialog = () => safeSetUiState(prev => ({ ...prev, openDialog: false }));
  const closeDoc = () => safeSetUiState(prev => ({ ...prev, pdf: false }));
  const closeImage = () => safeSetUiState(prev => ({ ...prev, image: false }));
  const handleCloseInfoDialog = () => {
    safeSetUiState(prev => ({ ...prev, openInfoDialog: false }));

    if (uiState.showCountdown) {
      safeSetUiState(prev => ({ ...prev, showCountdown: false, countdown: null }));
    }
  };

  return (
    <div className='send-email'>
      <Header />

      <div className="send-email__container">
        <div className="send-email__header">
          <h1 className='send-email__title'>Envoi de mail</h1>
        </div>

        <div className="send-email__content">
          <MailParams
            {...mailData}
            disableButton={uiState.disableButton}
            user={user}
            handleChange={handleChange}
            handleFileChange={handleFileChange}
            handleRemoveFile={handleRemoveFile}
            handleOpenDialog={handleOpenDialog}
            handleAutoSelect={handleAutoSelect}
            handleResetForm={handleResetForm}
            showPDF={showPDF}
            showImage={showImage}
            getFileType={getFileType}
            fileMetadata={fileMetadata}
          />
        </div>
      </div>

      <GenPdf
        file={uiState.file}
        isOpen={uiState.pdf}
        closeDoc={closeDoc}
      />

      {/* Affichage des images */}
      {uiState.image && uiState.imageFile && (
        <div className="send-email__image-overlay" onClick={closeImage}>
          <div className="send-email__image-modal" onClick={(e) => e.stopPropagation()}>
            <div className="send-email__image-header">
              <h3 className="send-email__image-title">{uiState.imageFile.name}</h3>
              <button
                className="send-email__image-close"
                onClick={closeImage}
                aria-label="Fermer l'image"
              >
                <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                  <line x1="18" y1="6" x2="6" y2="18"></line>
                  <line x1="6" y1="6" x2="18" y2="18"></line>
                </svg>
              </button>
            </div>
            <div className="send-email__image-content">
              <img
                src={URL.createObjectURL(uiState.imageFile)}
                alt={uiState.imageFile.name}
                className="send-email__image-display"
                onLoad={() => URL.revokeObjectURL(URL.createObjectURL(uiState.imageFile))}
              />
            </div>
          </div>
        </div>
      )}

      <ConfirmMail
        open={uiState.openDialog}
        onConfirm={handleSubmit}
        setOpen={handleCloseDialog}
        getTo={mailData.to}
        getSubject={mailData.subject}
        getText={mailData.text}
        getSender={mailData.sender}
      />

      <InformationDialog
        isOpen={uiState.openInfoDialog}
        title="INFORMATION"
        information={getDialogInformation()}
        handleClose={handleCloseInfoDialog}
      />
    </div>
  );
};

const mapStateToProps = state => ({
  user: state.user.items,
});

export default connect(mapStateToProps)(SendMail);